import React, { useCallback, useMemo } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { Checkbox, Switch } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { useTheme } from 'styled-components';
import { useQuestionTypes } from '../../contexts/QuestionTypesContext';
import {
  DEFAULT_FORM_EDITING_LOCALE,
  denormalizeQuestionForEditingLocale,
  mergePartialQuestionDataForLocale,
} from '../../helpers/localizedQuestionFieldHelpers';
import {
  getTrimmedLength,
  QUESTION_DESCRIPTION_MAX_LENGTH,
  QUESTION_TEXT_MAX_LENGTH_COI,
  QUESTION_TEXT_MAX_LENGTH_SPEAKUP,
} from '../../helpers/validation';
import { useFormType } from '../../hooks';
import { ANSWER_TYPES, CHOICE_BASED_TYPES, QuestionData } from '../../types';
import { OptionsList } from '../OptionsList';
import {
  buildAnswerTypeOptions,
  buildDatabaseFieldSubTypeOptions,
} from './buildQuestionCardDropdownOptions';
import { DatabaseFieldSection } from './DatabaseFieldSection';
import {
  answerTypeConfig,
  createQuestionHandlers,
  getDatabaseFieldError,
  getDescriptionCharCount,
  getEffectiveOptionsError,
  getQuestionDescriptionError,
  getQuestionTextError,
} from './helpers';
import {
  CharacterCounter,
  DescriptionFieldWrapper,
  DragHandle,
  ExpandCollapseButton,
  FieldInputWrapper,
  FieldLabelRow,
  FieldMetaContainer,
  FormField,
  FormLabel,
  HeaderControlGroup,
  PreviewText,
  QuestionCardActions,
  QuestionCardContainer,
  QuestionCardContent,
  QuestionCardHeader,
  QuestionCardWrapper,
  QuestionFieldsWrapper,
  QuestionTextFieldColumn,
  RequiredBadge,
  RequiredMark,
  StyledActionIconButton,
  StyledErrorMessage,
  SwitchWrapper,
} from './QuestionCard.styles';

interface Props {
  question: QuestionData;
  index: number;
  isLastQuestion?: boolean;
  allQuestions?: QuestionData[];
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onChange: (id: string, data: Partial<QuestionData>) => void;
  editingLocale?: string;
  uiCopy?: QuestionCardCopy;
  'data-testid'?: string;
}

export type QuestionCardCopy = {
  requiredSuffix: string;
};

const ENGLISH_QUESTION_CARD_COPY: QuestionCardCopy = {
  requiredSuffix: '(Required)',
};

export const QuestionCard = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      question,
      index,
      isLastQuestion = false,
      allQuestions = [],
      onDelete,
      onDuplicate,
      onChange,
      editingLocale,
      uiCopy,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const formType = useFormType();
    const isSpeakUp = formType === FormType.SPEAK_UP;
    const questionCardCopy = editingLocale
      ? (uiCopy ?? ENGLISH_QUESTION_CARD_COPY)
      : ENGLISH_QUESTION_CARD_COPY;

    const { data: questionTypesData } = useQuestionTypes();

    const effectiveQuestion = useMemo(() => {
      if (!editingLocale || editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
        return question;
      }
      return denormalizeQuestionForEditingLocale(question, editingLocale);
    }, [editingLocale, question]);

    const translationRowForLocale = useMemo(() => {
      if (!editingLocale || editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
        return undefined;
      }
      return question.translations?.find(
        (t) => t.languageCode === editingLocale,
      );
    }, [editingLocale, question.translations]);

    const wrappedOnChange = useCallback(
      (id: string, patch: Partial<QuestionData>) => {
        if (!editingLocale || editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
          onChange(id, patch);
          return;
        }
        onChange(
          id,
          mergePartialQuestionDataForLocale(question, editingLocale, patch),
        );
      },
      [editingLocale, question, onChange],
    );

    const handlers = createQuestionHandlers(
      effectiveQuestion,
      wrappedOnChange,
      onDelete,
      onDuplicate,
    );

    const answerTypeOptions = useMemo(
      () => buildAnswerTypeOptions(questionTypesData),
      [questionTypesData],
    );

    const databaseFieldSubTypeOptions = useMemo(
      () => buildDatabaseFieldSubTypeOptions(questionTypesData),
      [questionTypesData],
    );

    const isExpanded =
      question.isExpanded !== undefined ? question.isExpanded : true;

    const selectedAnswerType = question.questionType
      ? answerTypeOptions.find((opt) => opt.value === question.questionType)
      : null;

    const previewText =
      effectiveQuestion.questionText || `Question ${question.displayNumber}`;

    const textError = useMemo(
      () =>
        getQuestionTextError(
          question,
          effectiveQuestion.questionText,
          isSpeakUp,
          editingLocale,
          translationRowForLocale,
        ),
      [
        question,
        effectiveQuestion.questionText,
        isSpeakUp,
        editingLocale,
        translationRowForLocale,
      ],
    );

    const descriptionError = useMemo(
      () =>
        getQuestionDescriptionError(
          question,
          effectiveQuestion.description,
          isSpeakUp,
          editingLocale,
          translationRowForLocale,
        ),
      [
        question,
        effectiveQuestion.description,
        isSpeakUp,
        editingLocale,
        translationRowForLocale,
      ],
    );

    const descriptionCharCount = useMemo(
      () => getDescriptionCharCount(effectiveQuestion.description),
      [effectiveQuestion.description],
    );

    const databaseFieldError = useMemo(
      () => getDatabaseFieldError(question),
      [question],
    );

    const hasTextError = !!textError;

    const hasAnswerTypeError =
      question.answerTypeTouched && !question.questionType;

    const isChoiceBasedQuestion = CHOICE_BASED_TYPES.has(
      question.questionType || '',
    );

    const currentConfig = question.questionType
      ? answerTypeConfig[question.questionType]
      : null;

    const effectiveOptionsError = useMemo(
      () =>
        getEffectiveOptionsError(
          question,
          isSpeakUp,
          editingLocale,
          translationRowForLocale,
        ),
      [question, isSpeakUp, editingLocale, translationRowForLocale],
    );

    return (
      <Draggable draggableId={question.id} index={index}>
        {(provided, snapshot) => (
          <QuestionCardWrapper
            data-testid={`${props['data-testid']}-wrapper`}
            ref={(el: HTMLDivElement | null) => {
              provided.innerRef(el);
              if (typeof ref === 'function') ref(el);
              else if (ref)
                (ref as React.MutableRefObject<HTMLDivElement | null>).current =
                  el;
            }}
            {...provided.draggableProps}
          >
            <QuestionCardContainer
              data-testid={props['data-testid']}
              $isCollapsed={!isExpanded}
              onClick={!isExpanded ? handlers.handleToggleExpand : undefined}
              onKeyDown={!isExpanded ? handlers.handleKeyDown : undefined}
              role={!isExpanded ? 'button' : undefined}
              tabIndex={!isExpanded ? 0 : undefined}
              aria-expanded={!isExpanded ? isExpanded : undefined}
              aria-label={
                !isExpanded ? `Expand question: ${previewText}` : undefined
              }
            >
              <QuestionCardHeader
                data-testid={`${props['data-testid']}-header`}
                $isCollapsed={!isExpanded}
                onClick={isExpanded ? handlers.handleToggleExpand : undefined}
                onKeyDown={isExpanded ? handlers.handleKeyDown : undefined}
                role={isExpanded ? 'button' : undefined}
                tabIndex={isExpanded ? 0 : undefined}
                aria-expanded={isExpanded ? isExpanded : undefined}
                aria-label={
                  isExpanded ? `Collapse question: ${previewText}` : undefined
                }
              >
                <HeaderControlGroup>
                  <DragHandle
                    data-testid={`${props['data-testid']}-drag-handle`}
                    {...provided.dragHandleProps}
                  >
                    <StyledIcon
                      className="uil uil-draggabledots"
                      size="24px"
                      color={
                        theme.isVaultVariant
                          ? theme.newPalette.neutral.N800
                          : theme.atlas.semantic.color.type.default
                      }
                    />
                  </DragHandle>
                  <ExpandCollapseButton
                    data-testid={`${props['data-testid']}-expand-collapse-button`}
                    aria-label={
                      isExpanded ? 'Collapse question' : 'Expand question'
                    }
                  >
                    <StyledIcon
                      className={
                        isExpanded ? 'uil uil-angle-up' : 'uil uil-angle-down'
                      }
                      size="24px"
                      color={
                        theme.isVaultVariant
                          ? theme.newPalette.neutral.N800
                          : theme.atlas.semantic.color.type.default
                      }
                    />
                  </ExpandCollapseButton>
                </HeaderControlGroup>
                <PreviewText
                  data-testid={`${props['data-testid']}-preview-text`}
                >
                  {previewText}
                  {question.isRequired && (
                    <RequiredBadge
                      data-testid={`${props['data-testid']}-required-badge`}
                    >
                      {' '}
                      {questionCardCopy.requiredSuffix}
                    </RequiredBadge>
                  )}
                </PreviewText>
                {!snapshot.isDragging && (
                  <QuestionCardActions
                    data-testid={`${props['data-testid']}-actions`}
                  >
                    <StyledActionIconButton
                      size="small"
                      variant="tertiary"
                      iconName="uil uil-copy"
                      noBorder
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        handlers.handleDuplicateClick();
                      }}
                      data-testid={`${props['data-testid']}-duplicate-button`}
                      aria-label="Duplicate question"
                    />
                    <StyledActionIconButton
                      size="small"
                      variant="tertiary"
                      iconName="uil uil-trash-alt"
                      noBorder
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        handlers.handleDeleteClick();
                      }}
                      data-testid={`${props['data-testid']}-delete-button`}
                      aria-label="Delete question"
                    />
                  </QuestionCardActions>
                )}
              </QuestionCardHeader>

              {isExpanded && (
                <QuestionCardContent
                  data-testid={`${props['data-testid']}-content`}
                >
                  <QuestionFieldsWrapper
                    data-testid={`${props['data-testid']}-fields`}
                  >
                    <QuestionTextFieldColumn>
                      <FormField>
                        <FieldLabelRow>
                          <FormLabel
                            htmlFor={`${props['data-testid']}-text-input`}
                          >
                            Question text
                            <RequiredMark>*</RequiredMark>
                          </FormLabel>
                          <CharacterCounter
                            isError={
                              getTrimmedLength(effectiveQuestion.questionText) >
                              (!isSpeakUp
                                ? QUESTION_TEXT_MAX_LENGTH_COI
                                : QUESTION_TEXT_MAX_LENGTH_SPEAKUP)
                            }
                            data-testid={`${props['data-testid']}-text-character-counter`}
                            aria-live="polite"
                          >
                            {getTrimmedLength(effectiveQuestion.questionText)}/
                            {!isSpeakUp
                              ? QUESTION_TEXT_MAX_LENGTH_COI
                              : QUESTION_TEXT_MAX_LENGTH_SPEAKUP}
                          </CharacterCounter>
                        </FieldLabelRow>
                        <FieldInputWrapper>
                          <TextField
                            fullWidth
                            value={effectiveQuestion.questionText}
                            onChange={(e) =>
                              handlers.handleTextChange(e.target.value)
                            }
                            placeholder="Enter question text"
                            error={hasTextError}
                            slotProps={{
                              htmlInput: {
                                id: `${props['data-testid']}-text-input`,
                                'data-testid': `${props['data-testid']}-text-input`,
                                'aria-invalid': hasTextError,
                                'aria-describedby': hasTextError
                                  ? `${props['data-testid']}-text-error`
                                  : undefined,
                              } as Record<string, unknown>,
                            }}
                          />
                          {textError && (
                            <FieldMetaContainer>
                              <StyledErrorMessage
                                id={`${props['data-testid']}-text-error`}
                                role="alert"
                                data-testid={`${props['data-testid']}-text-error-message`}
                              >
                                {textError}
                              </StyledErrorMessage>
                            </FieldMetaContainer>
                          )}
                        </FieldInputWrapper>
                      </FormField>
                    </QuestionTextFieldColumn>
                    <SwitchWrapper
                      data-testid={`${props['data-testid']}-switch-wrapper`}
                    >
                      <Switch
                        checked={question.isRequired}
                        onClick={handlers.handleRequiredChange}
                        label="Required"
                        labelPosition="right"
                        data-testid={`${props['data-testid']}-required-switch`}
                      />
                    </SwitchWrapper>
                  </QuestionFieldsWrapper>

                  <DescriptionFieldWrapper>
                    <FormField>
                      <FieldLabelRow>
                        <FormLabel
                          htmlFor={`${props['data-testid']}-description-input`}
                        >
                          Description
                        </FormLabel>
                        <CharacterCounter
                          isError={
                            descriptionCharCount >
                            QUESTION_DESCRIPTION_MAX_LENGTH
                          }
                          data-testid={`${props['data-testid']}-description-character-counter`}
                          aria-live="polite"
                        >
                          {descriptionCharCount}/
                          {QUESTION_DESCRIPTION_MAX_LENGTH}
                        </CharacterCounter>
                      </FieldLabelRow>
                      <FieldInputWrapper>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={effectiveQuestion.description}
                          onChange={(e) =>
                            handlers.handleDescriptionChange(e.target.value)
                          }
                          placeholder="Additional context or instructions."
                          error={!!descriptionError}
                          slotProps={{
                            htmlInput: {
                              id: `${props['data-testid']}-description-input`,
                              'data-testid': `${props['data-testid']}-description-input`,
                              'aria-invalid': !!descriptionError,
                              'aria-describedby': descriptionError
                                ? `${props['data-testid']}-description-error`
                                : undefined,
                            } as Record<string, unknown>,
                          }}
                        />
                        {descriptionError && (
                          <FieldMetaContainer>
                            <StyledErrorMessage
                              id={`${props['data-testid']}-description-error`}
                              role="alert"
                              data-testid={`${props['data-testid']}-description-error-message`}
                            >
                              {descriptionError}
                            </StyledErrorMessage>
                          </FieldMetaContainer>
                        )}
                      </FieldInputWrapper>
                    </FormField>
                  </DescriptionFieldWrapper>

                  <FormField>
                    <FieldLabelRow>
                      <FormLabel
                        htmlFor={`${props['data-testid']}-answer-type-dropdown`}
                      >
                        Question type
                        <RequiredMark>*</RequiredMark>
                      </FormLabel>
                    </FieldLabelRow>
                    <FieldInputWrapper>
                      <div
                        data-testid={`${props['data-testid']}-answer-type-dropdown`}
                      >
                        <Select
                          fullWidth
                          displayEmpty
                          value={selectedAnswerType?.value ?? ''}
                          onChange={(e: SelectChangeEvent<string>) => {
                            const opt = answerTypeOptions.find(
                              (o) => o.value === e.target.value,
                            );
                            if (opt) handlers.handleAnswerTypeChange(opt);
                          }}
                          error={hasAnswerTypeError}
                          inputProps={{
                            id: `${props['data-testid']}-answer-type-dropdown-input`,
                            'aria-invalid': hasAnswerTypeError,
                            'aria-describedby': hasAnswerTypeError
                              ? `${props['data-testid']}-answer-type-error`
                              : undefined,
                          }}
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ opacity: 0.5 }}>
                                  Choose a question type
                                </span>
                              );
                            }
                            return (
                              answerTypeOptions.find(
                                (o) => o.value === selected,
                              )?.label ?? selected
                            );
                          }}
                        >
                          {answerTypeOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                      {hasAnswerTypeError && (
                        <FieldMetaContainer>
                          <StyledErrorMessage
                            id={`${props['data-testid']}-answer-type-error`}
                            role="alert"
                            data-testid={`${props['data-testid']}-answer-type-error-message`}
                          >
                            Question type is required
                          </StyledErrorMessage>
                        </FieldMetaContainer>
                      )}
                    </FieldInputWrapper>
                  </FormField>

                  {isChoiceBasedQuestion && currentConfig && (
                    <OptionsList
                      options={effectiveQuestion.options}
                      answerType={question.questionType}
                      config={currentConfig}
                      optionsError={effectiveOptionsError}
                      onAddFirstOption={handlers.handleAddFirstOption}
                      onAddOption={handlers.handleAddOption}
                      onOptionTextChange={handlers.handleOptionTextChange}
                      onDeleteOption={handlers.handleDeleteOption}
                      onToggleOptionFlagged={handlers.handleToggleOptionFlagged}
                      showOptionFlagged={!isSpeakUp}
                      showDataGridFlagIfAnswered={
                        !isSpeakUp &&
                        question.questionType === ANSWER_TYPES.DATA_GRID
                      }
                      flagIfAnswered={question.flagIfAnswered}
                      onFlagIfAnsweredChange={
                        handlers.handleFlagIfAnsweredChange
                      }
                      hideOptionFlowControls={false}
                      onNextActionChange={handlers.handleNextActionChange}
                      isLastQuestion={isLastQuestion}
                      questionIndex={index}
                      allQuestions={allQuestions}
                      data-testid={props['data-testid']}
                    />
                  )}

                  {question.questionType === ANSWER_TYPES.DATABASE_FIELD && (
                    <DatabaseFieldSection
                      question={question}
                      databaseFieldSubTypeOptions={databaseFieldSubTypeOptions}
                      databaseFieldError={databaseFieldError}
                      onDatabaseFieldChange={
                        handlers.handleDatabaseFieldChange as (
                          value: unknown,
                        ) => void
                      }
                      onDatabaseMultiSelectToggle={
                        handlers.handleDatabaseMultiSelectToggle
                      }
                      requiredSuffix={questionCardCopy.requiredSuffix}
                      data-testid={props['data-testid']}
                    />
                  )}

                  {!isSpeakUp &&
                    question.questionType &&
                    !isChoiceBasedQuestion && (
                      <Checkbox
                        checked={question.flagIfAnswered ?? false}
                        onChange={handlers.handleFlagIfAnsweredChange}
                        label={
                          question.questionType === ANSWER_TYPES.ATTACHMENT
                            ? 'Flag if attachment provided'
                            : 'Flag if answered'
                        }
                        labelPosition="right"
                        clickableRow
                        $fullWidthLabel
                        data-testid={`${props['data-testid']}-flag-if-answered`}
                      />
                    )}
                </QuestionCardContent>
              )}
            </QuestionCardContainer>
          </QuestionCardWrapper>
        )}
      </Draggable>
    );
  },
);

QuestionCard.displayName = 'QuestionCard';
